import { useCallback, useMemo } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import {
  dismissNewHypotheticalItemNotification,
  getNewHypotheticalItemNotification,
  setNewHypotheticalItemNotification,
} from '@/modules/estateWaterfall/EstateWaterfall.utils';
import {
  AugmentedCreateHypotheticalCltInput,
  AugmentedCreateHypotheticalCrtInput,
  AugmentedUpdateHypotheticalCltInput,
  AugmentedUpdateHypotheticalCrtInput,
} from '@/types/schema';

import { HypotheticalCharitableTrustModalKind } from '../EstateWaterfallHypotheticalCharitableTrustModal.types';
import { getHypotheticalCharitableTrustModalKindLabel } from '../EstateWaterfallHypotheticalCharitableTrustModal.utils';
import {
  CreateEstateWaterfallHypotheticalCltMutation,
  CreateEstateWaterfallHypotheticalCrtMutation,
  useCreateEstateWaterfallHypotheticalCltMutation,
  useCreateEstateWaterfallHypotheticalCrtMutation,
  useDeleteEstateWaterfallHypotheticalCltMutation,
  useDeleteEstateWaterfallHypotheticalCrtMutation,
  useUpdateEstateWaterfallHypotheticalCltMutation,
  useUpdateEstateWaterfallHypotheticalCrtMutation,
} from '../graphql/EstateWaterfallHypotheticalCharitableTrustModal.generated';

export function useMutations(
  kind: HypotheticalCharitableTrustModalKind,
  handleClose: () => void
) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const trustKindLabel = getHypotheticalCharitableTrustModalKindLabel(kind);

  const onError = useCallback(
    (
      error: Error,
      {
        action,
        label,
      }: { action: 'update' | 'create' | 'delete'; label: string }
    ) => {
      showFeedback(`Failed to ${action} hypothetical ${label}`);
      reportError(`Failed to ${action} hypothetical ${label}`, error);
    },
    [showFeedback, reportError]
  );

  const onSuccess = useCallback(
    (
      kind: 'created' | 'updated' | 'deleted',
      response?: {
        createdResponse?:
          | CreateEstateWaterfallHypotheticalCrtMutation
          | CreateEstateWaterfallHypotheticalCltMutation;
        deletedIds?: string[];
      }
    ) => {
      showFeedback(`Hypothetical ${trustKindLabel} ${kind}`, {
        variant: 'success',
      });

      if (
        response?.createdResponse &&
        'createHypotheticalCrt' in response.createdResponse &&
        response.createdResponse.createHypotheticalCrt?.id
      ) {
        setNewHypotheticalItemNotification({
          proposalId:
            response.createdResponse.createHypotheticalCrt.simulatedCrt.id,
          dismissed: false,
        });
      }

      if (
        response?.createdResponse &&
        'createHypotheticalClt' in response.createdResponse &&
        response.createdResponse.createHypotheticalClt?.id
      ) {
        setNewHypotheticalItemNotification({
          proposalId:
            response.createdResponse.createHypotheticalClt.simulatedClt.id,
          dismissed: false,
        });
      }

      response?.deletedIds?.forEach((id) => {
        const notification = getNewHypotheticalItemNotification();

        if (
          notification &&
          'proposalId' in notification &&
          notification.proposalId === id
        ) {
          dismissNewHypotheticalItemNotification();
        }
      });

      handleClose();
    },
    [showFeedback, trustKindLabel, handleClose]
  );

  const [updateHypotheticalCrt, updateHypotheticalCrtProps] =
    useUpdateEstateWaterfallHypotheticalCrtMutation({
      refetchQueries: 'active',
      onError: (error: Error) => {
        onError(error, { action: 'update', label: trustKindLabel });
      },
    });

  const [createHypotheticalCrt, createHypotheticalCrtProps] =
    useCreateEstateWaterfallHypotheticalCrtMutation({
      refetchQueries: 'active',
      onError: (error: Error) => {
        onError(error, { action: 'create', label: trustKindLabel });
      },
    });

  const [deleteHypotheticalCrt, deleteHypotheticalCrtProps] =
    useDeleteEstateWaterfallHypotheticalCrtMutation({
      refetchQueries: 'active',
      onError: (error: Error) => {
        onError(error, { action: 'delete', label: trustKindLabel });
      },
    });

  const [updateHypotheticalClt, updateHypotheticalCltProps] =
    useUpdateEstateWaterfallHypotheticalCltMutation({
      refetchQueries: 'active',
      onError: (error: Error) => {
        onError(error, { action: 'update', label: trustKindLabel });
      },
    });

  const [createHypotheticalClt, createHypotheticalCltProps] =
    useCreateEstateWaterfallHypotheticalCltMutation({
      refetchQueries: 'active',
      onError: (error: Error) => {
        onError(error, { action: 'create', label: trustKindLabel });
      },
    });

  const [deleteHypotheticalClt, deleteHypotheticalCltProps] =
    useDeleteEstateWaterfallHypotheticalCltMutation({
      refetchQueries: 'active',
      onError: (error: Error) => {
        onError(error, { action: 'delete', label: trustKindLabel });
      },
    });

  const update = useCallback(
    (
      input:
        | AugmentedUpdateHypotheticalCrtInput
        | AugmentedUpdateHypotheticalCltInput
    ) => {
      if (kind === HypotheticalCharitableTrustModalKind.CRT) {
        return updateHypotheticalCrt({
          variables: { input: input as AugmentedUpdateHypotheticalCrtInput },
          onCompleted: () => {
            onSuccess('updated');
          },
        });
      } else {
        return updateHypotheticalClt({
          variables: { input: input as AugmentedUpdateHypotheticalCltInput },
          onCompleted: () => {
            onSuccess('updated');
          },
        });
      }
    },
    [kind, updateHypotheticalCrt, onSuccess, updateHypotheticalClt]
  );

  const create = useCallback(
    (
      input:
        | AugmentedCreateHypotheticalCrtInput
        | AugmentedCreateHypotheticalCltInput
    ) => {
      if (kind === HypotheticalCharitableTrustModalKind.CRT) {
        return createHypotheticalCrt({
          variables: { input: input as AugmentedCreateHypotheticalCrtInput },
          onCompleted: (response) => {
            onSuccess('created', { createdResponse: response });
          },
        });
      } else {
        return createHypotheticalClt({
          variables: { input: input as AugmentedCreateHypotheticalCltInput },
          onCompleted: (response) => {
            onSuccess('created', { createdResponse: response });
          },
        });
      }
    },
    [kind, createHypotheticalCrt, createHypotheticalClt, onSuccess]
  );

  const deleteMutation = useCallback(
    (ids: string[]) => {
      if (kind === HypotheticalCharitableTrustModalKind.CRT) {
        return deleteHypotheticalCrt({
          variables: { hcrtIds: ids },
          onCompleted: () => {
            onSuccess('deleted', { deletedIds: ids });
          },
        });
      } else {
        return deleteHypotheticalClt({
          variables: { hcltIds: ids },
          onCompleted: () => {
            onSuccess('deleted', { deletedIds: ids });
          },
        });
      }
    },
    [kind, deleteHypotheticalCrt, deleteHypotheticalClt, onSuccess]
  );

  const updateProps = useMemo(() => {
    if (kind === HypotheticalCharitableTrustModalKind.CRT) {
      return updateHypotheticalCrtProps;
    } else {
      return updateHypotheticalCltProps;
    }
  }, [kind, updateHypotheticalCrtProps, updateHypotheticalCltProps]);

  const createProps = useMemo(() => {
    if (kind === HypotheticalCharitableTrustModalKind.CRT) {
      return createHypotheticalCrtProps;
    } else {
      return createHypotheticalCltProps;
    }
  }, [kind, createHypotheticalCrtProps, createHypotheticalCltProps]);

  const deleteProps = useMemo(() => {
    if (kind === HypotheticalCharitableTrustModalKind.CRT) {
      return deleteHypotheticalCrtProps;
    } else {
      return deleteHypotheticalCltProps;
    }
  }, [kind, deleteHypotheticalCrtProps, deleteHypotheticalCltProps]);

  return {
    update,
    create,
    delete: deleteMutation,
    updateProps,
    createProps,
    deleteProps,
  };
}
